import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LinkedinLogo, XLogo, GithubLogo } from '@phosphor-icons/react';
import { WEB3FORMS_ACCESS_KEY } from '../../utils/web3forms';
import '../../styles/aurablox-navigation.css';

const RELAUNCH_POSTS = [
  {
    webp: '/images/relaunch/01-we-are-back.webp',
    png: '/images/relaunch/01-we-are-back.png',
    alt: 'Black grid and three vertical panels on paper white, crossed by a vermilion brush stroke reading “We are back.”',
  },
  {
    webp: '/images/relaunch/02-ai-opportunity.webp',
    png: '/images/relaunch/02-ai-opportunity.png',
    alt: 'Black architectural composition with a white opening and red brush stroke reading “Where does AI actually help?”',
  },
  {
    webp: '/images/relaunch/03-audit.webp',
    png: '/images/relaunch/03-audit.png',
    alt: 'Paper white technical grid and a red ink circle beside the headline “Find the signal.”',
  },
  {
    webp: '/images/relaunch/04-roadmap.webp',
    png: '/images/relaunch/04-roadmap.png',
    alt: 'Three dark folding-screen panels with a winding red route reading “From idea to roadmap.”',
  },
  {
    webp: '/images/relaunch/05-build.webp',
    png: '/images/relaunch/05-build.png',
    alt: 'Black architectural panels, white structure, and red brush stroke reading “Build what matters.”',
  },
  {
    webp: '/images/relaunch/06-agents.webp',
    png: '/images/relaunch/06-agents.png',
    alt: 'White field with black geometric nodes connected by a red brush line reading “Agents with guardrails.”',
  },
  {
    webp: '/images/relaunch/07-data.webp',
    png: '/images/relaunch/07-data.png',
    alt: 'Tangled black lines resolving into ordered columns across three panels reading “Make data ready.”',
  },
  {
    webp: '/images/relaunch/08-adoption.webp',
    png: '/images/relaunch/08-adoption.png',
    alt: 'Red brush path leading through dark geometric panels toward a white field reading “Adoption is the work.”',
  },
  {
    webp: '/images/relaunch/09-governance.webp',
    png: '/images/relaunch/09-governance.png',
    alt: 'Dark three-panel composition with a red circular mark and seal reading “Control builds trust.”',
  },
  {
    webp: '/images/relaunch/10-conversation.webp',
    png: '/images/relaunch/10-conversation.png',
    alt: 'Paper white composition with a red brush gesture and fine black grids reading “What should we build next?”',
  },
  {
    webp: '/images/relaunch/11-one-workflow.webp',
    png: '/images/relaunch/11-one-workflow.png',
    alt: 'Red route winding through a dense black grid toward an opening reading “Start with one workflow.”',
  },
  {
    webp: '/images/relaunch/12-build-vs-buy.webp',
    png: '/images/relaunch/12-build-vs-buy.png',
    alt: 'Two contrasting black geometric structures connected by a red brush line reading “Build or buy?”',
  },
  {
    webp: '/images/relaunch/13-measure-impact.webp',
    png: '/images/relaunch/13-measure-impact.png',
    alt: 'Black, white, and red poster reading “Measure what changes. Outcomes over output.”',
  },
  {
    webp: '/images/relaunch/14-real-world.webp',
    png: '/images/relaunch/14-real-world.png',
    alt: 'Architectural wireframe becoming solid black structure across three panels reading “Beyond the demo.”',
  },
  {
    webp: '/images/relaunch/15-ai-literacy.webp',
    png: '/images/relaunch/15-ai-literacy.png',
    alt: 'Black geometric modules progressing beneath a red brush arc reading “Build AI fluency.”',
  },
  {
    webp: '/images/relaunch/16-integration.webp',
    png: '/images/relaunch/16-integration.png',
    alt: 'Black architectural grids meeting at a red joint across three panels reading “Make it fit.”',
  },
  {
    webp: '/images/relaunch/17-human-control.webp',
    png: '/images/relaunch/17-human-control.png',
    alt: 'Sweeping red path moving through a restrained black grid on paper white reading “Keep humans in charge.”',
  },
  {
    webp: '/images/relaunch/18-ownership.webp',
    png: '/images/relaunch/18-ownership.png',
    alt: 'Three dark screen panels with technical lines and a central red square reading “Who owns the outcome?”',
  },
  {
    webp: '/images/relaunch/19-momentum.webp',
    png: '/images/relaunch/19-momentum.png',
    alt: 'Rising black geometric blocks beneath a sweeping red brush stroke reading “Momentum is built.”',
  },
  {
    webp: '/images/relaunch/20-start-here.webp',
    png: '/images/relaunch/20-start-here.png',
    alt: 'Red brush gesture moving from a dark grid into white space reading “Let’s make it real.”',
  },
];

const SOCIAL_LINKS = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/jjohnso75/', Icon: LinkedinLogo },
  { label: 'X (Twitter)', href: 'https://x.com/jahflyx', Icon: XLogo },
  { label: 'GitHub', href: 'https://github.com/murderszn', Icon: GithubLogo },
];

const STORAGE_KEY = 'aurablox_relaunch_post_index';

const advanceRelaunchIndex = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw !== null ? Number.parseInt(raw, 10) : -1;
    const next = Number.isNaN(parsed) ? 0 : (parsed + 1) % RELAUNCH_POSTS.length;
    window.localStorage.setItem(STORAGE_KEY, String(next));
    return next;
  } catch {
    return 0;
  }
};

const INITIAL_POST_INDEX = typeof window !== 'undefined' ? advanceRelaunchIndex() : 0;

const Footer = () => {
  const { pathname } = useLocation();
  const year = new Date().getFullYear();
  const [postIndex, setPostIndex] = useState(INITIAL_POST_INDEX);
  const lastPathRef = useRef(pathname);

  useEffect(() => {
    if (lastPathRef.current !== pathname) {
      lastPathRef.current = pathname;
      setPostIndex(advanceRelaunchIndex());
    }
  }, [pathname]);

  const art = pathname === '/' ? (RELAUNCH_POSTS[postIndex] || RELAUNCH_POSTS[0]) : { png: '/images/aurablox-constructivist.png', alt: 'Abstract architectural geometry in black, white, and red' };
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const handleInquiry = async (event) => {
    event.preventDefault();
    setSending(true);
    setStatus(null);
    try {
      const form = event.currentTarget;
      const data = new FormData(form);
      const res = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: data });
      if (!res.ok) throw new Error('failed');
      setStatus({ type: 'ok', message: 'Thanks — your reply request has been sent.' });
      setEmail('');
    } catch {
      setStatus({ type: 'err', message: 'Something went wrong. Try emailing jjohnson@aurablox.com directly.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className={`footer ab-footer${pathname !== '/' ? ' footer--quiet' : ''}`} id="contact">
      <div className="ab-footer__shell">
        {/* ─── TIER 1: TOP CONVERSATION CTA (LEFT) & ROTATING RELAUNCH ART (RIGHT) ─── */}
        {pathname !== '/contact' && <div className="ab-footer__top">
          <div className="ab-footer__info-zone">
            <section className="ab-footer__col ab-footer__col--cta" aria-labelledby="footer-cta-heading">
              <div className="ab-footer__section-title">START A CONVERSATION</div>
              <h2 id="footer-cta-heading">Let’s fix the data foundation.</h2>
              <p className="ab-footer__lead">
                A reporting deadline, fragmented feeds, or inconsistent master data. Tell us where the foundation needs to improve.
              </p>
              {pathname !== '/contact' && (
                <form className="cta-block__form" onSubmit={handleInquiry}>
                  <input type="hidden" name="access_key" value={WEB3FORMS_ACCESS_KEY} />
                  <input type="hidden" name="subject" value="Commercial Data Foundation Reply Request" />
                  <input type="checkbox" name="botcheck" style={{ display: 'none' }} readOnly />
                  <input
                    type="email"
                    name="email"
                    placeholder="your.email@company.com"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-label="Email address"
                    aria-describedby="footer-email-helper"
                  />
                  <button type="submit" className="btn btn--primary" disabled={sending}>
                    {sending ? 'Sending…' : 'Request a reply'}
                  </button>
                </form>
              )}
              {pathname !== '/contact' && (
                <p className="cta-block__helper" id="footer-email-helper">
                  We’ll use your email to reply about your data foundation.
                </p>
              )}
              {pathname !== '/contact' && status && (
                <p role="status" className={`cta-block__status cta-block__status--${status.type}`}>
                  {status.message}
                </p>
              )}
              <div className="ab-footer__cta-links">
                <p className="cta-block__direct">
                  Prefer email? <a href="mailto:jjohnson@aurablox.com">Write to jjohnson@aurablox.com</a>
                </p>
                {pathname !== '/contact' && (
                  <p className="cta-block__inquiry">
                    <Link to="/contact#inquiry">
                      Open the full inquiry form <span aria-hidden="true">↗</span>
                    </Link>
                  </p>
                )}
              </div>
            </section>
          </div>

          <div className="ab-footer__poster-zone">
            <figure className="cta-block__media ab-footer__poster">
              <picture>
                {art.webp && <source srcSet={art.webp} type="image/webp" />}
                <img
                  src={art.png}
                  alt={art.alt}
                  loading="lazy"
                />
              </picture>
            </figure>
          </div>
        </div>

        }

        {/* ─── TIER 2: MONUMENTAL COMPANY WORDMARK (LEFT-JUSTIFIED, NO RED DOT) ─── */}
        <div className="ab-footer__logo-wrap" aria-label="AURABLOX">
          <div className="ab-footer__giant-wordmark" aria-hidden="true">
            <span className="ab-footer__giant-text">AURABLOX</span>
          </div>
        </div>

        {/* ─── TIER 3: BOTTOM COPYRIGHT, LINKS & SOCIAL ICONS ─── */}
        <div className="ab-footer__bottom-bar">
          <div className="ab-footer__copyright-wrap">
            <span className="ab-footer__copyright">© {year} AURABLOX</span>
            <span className="ab-footer__divider" aria-hidden="true">·</span>
            <nav className="footer__links" aria-label="Footer">
              {pathname !== '/' && <Link to="/">Home</Link>}
              {[
                ['/solutions', 'The engagement'],
                ['/training', 'Enablement'],
                ['/technology', 'Our work'],
                ['/about', 'About'],
                ['/contact', 'Contact'],
              ].map(([path, label]) => (
                pathname === path ? (
                  <span key={path} aria-current="page">{label}</span>
                ) : (
                  <Link key={path} to={path === '/contact' ? '/contact#inquiry' : path}>{label}</Link>
                )
              ))}
              <button
                type="button"
                className="ab-footer__prefs-trigger"
                onClick={() => window.dispatchEvent(new CustomEvent('aurablox:open-privacy-preferences'))}
              >
                Privacy &amp; Opt-Out
              </button>
            </nav>
          </div>

          <ul className="ab-footer__socials" aria-label="AURABLOX social media and online links">
            {SOCIAL_LINKS.map(({ label, href, Icon }) => (
              <li key={label}>
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  title={label}
                  className="ab-footer__social-link"
                >
                  <Icon size={20} weight="regular" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

